import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";

// Origem do embedded signup da PilotStatus (usada p/ validar o postMessage)
const CONNECT_ORIGIN = "https://connect.pilotstatus.com.br";

const PilotConnectModal = ({ open, onClose, onCreated }) => {
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [connectUrl, setConnectUrl] = useState("");

  useEffect(() => {
    if (!open) {
      setName("");
      setLoading(false);
      setConnectUrl("");
    }
  }, [open]);

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.warn("Dê um nome para a conexão.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/pilot", { name });
      if (data && data.connectUrl) {
        setConnectUrl(data.connectUrl);
      } else {
        toast.error("Não foi possível gerar o link de conexão.");
      }
    } catch (err) {
      const detail =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Erro ao criar conexão";
      if (String(detail).includes("INSUFFICIENT_FUNDS")) {
        toast.error(
          "Saldo insuficiente na conta do provedor. Adicione crédito para conseguir conectar um número."
        );
      } else {
        toast.error("Erro ao criar conexão: " + detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const onMessage = useCallback(
    (event) => {
      if (event.origin !== CONNECT_ORIGIN) return;
      const msg = event.data || {};
      if (msg.source !== "pilot-status-embed") return;
      if (msg.type === "connect:paired") {
        toast.success("Número conectado com sucesso!");
        if (onCreated) onCreated();
        onClose();
      }
    },
    [onClose, onCreated]
  );

  useEffect(() => {
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [onMessage]);

  const iframeSrc = connectUrl
    ? connectUrl +
      (connectUrl.indexOf("?") !== -1 ? "&" : "?") +
      "embed=1&mode=button&parentOrigin=" +
      encodeURIComponent(window.location.origin)
    : "";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(connectUrl);
      toast.success("Link copiado! Cole em um navegador logado no Facebook.");
    } catch (e) {
      toast.info("Copie o link manualmente (Ctrl+C).");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Conectar WhatsApp Oficial</DialogTitle>
      <DialogContent dividers>
        {!connectUrl ? (
          <Box>
            <Typography
              variant="body2"
              style={{ marginBottom: 12, color: "#6b7280" }}
            >
              Dê um nome para identificar esta conexão. Em seguida você conecta o
              número do WhatsApp pela página oficial (sem QR Code, sem risco de
              bloqueio).
            </Typography>
            <TextField
              label="Nome da conexão"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              autoFocus
              disabled={loading}
            />
          </Box>
        ) : (
          <Box>
            <Typography
              variant="body2"
              style={{ marginBottom: 8, color: "#374151" }}
            >
              <b>Conexão criada!</b> Conecte o número do WhatsApp Business na
              janela abaixo (login com Facebook).
            </Typography>
            <iframe
              title="Conectar WhatsApp"
              src={iframeSrc}
              style={{
                width: "100%",
                height: 470,
                border: "1px solid #eef0f2",
                borderRadius: 10,
              }}
              sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms"
            />
            <Box
              style={{
                marginTop: 12,
                paddingTop: 10,
                borderTop: "1px solid #f0f0f0",
              }}
            >
              <Typography
                variant="caption"
                style={{ display: "block", color: "#9ca3af", marginBottom: 6 }}
              >
                Não está logado no Facebook neste navegador? Copie o link e abra
                em outro:
              </Typography>
              <Box style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button variant="outlined" size="small" onClick={handleCopy}>
                  Copiar link
                </Button>
                <Button
                  variant="text"
                  size="small"
                  href={connectUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Abrir em nova aba
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Fechar
        </Button>
        {!connectUrl && (
          <Button
            onClick={handleCreate}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : "Criar e conectar"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PilotConnectModal;
